import { Check, ImageDown } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"

interface Props {
  url: string
  contentType?: string | null
}

function isImageContentType(contentType?: string | null) {
  return contentType?.startsWith("image/") ?? false
}

async function blobAsPng(blob: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(blob)
  const canvas = document.createElement("canvas")
  canvas.width = bitmap.width
  canvas.height = bitmap.height
  const context = canvas.getContext("2d")
  if (!context) {
    throw new Error("Canvas 2D context unavailable")
  }
  context.drawImage(bitmap, 0, 0)
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((result) => {
      if (result) {
        resolve(result)
      } else {
        reject(new Error("Failed to encode image as PNG"))
      }
    }, "image/png")
  })
}

export default function CopyImageButton({ url, contentType }: Props) {
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState<string | null>(null)
  const canCopy =
    typeof navigator !== "undefined" &&
    typeof navigator.clipboard?.write === "function" &&
    typeof window !== "undefined" &&
    typeof window.ClipboardItem !== "undefined"

  if (!isImageContentType(contentType) || !canCopy) {
    return null
  }

  async function handleCopy() {
    setCopyError(null)
    try {
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`Fetch failed with status ${response.status}`)
      }
      const original = await response.blob()
      const supportsType =
        typeof window.ClipboardItem.supports === "function"
          ? window.ClipboardItem.supports(original.type)
          : original.type === "image/png"
      const blob = supportsType ? original : await blobAsPng(original)
      const item = new window.ClipboardItem({ [blob.type]: blob })
      await navigator.clipboard.write([item])
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopyError("Couldn't copy image — try downloading instead.")
    }
  }

  const label = copyError ?? (copied ? "Copied" : "Copy image")

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={label}
          onClick={(event) => {
            event.stopPropagation()
            void handleCopy()
          }}
          className="bg-background/80 text-foreground hover:bg-background absolute top-2 right-2 rounded-md shadow-sm"
        >
          {copied ? (
            <Check className="size-3.5" aria-hidden />
          ) : (
            <ImageDown className="size-3.5" aria-hidden />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="left">{label}</TooltipContent>
    </Tooltip>
  )
}
